///
/// EarthBrowser
///
/// Created by Matt Giger on 2/2/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type Material } from "@stovokor/shader/Material"
import { vec3, vec4 } from "@stovokor/math/vector"
import { matrix } from "@stovokor/math/matrix"
import { type Texture } from "@stovokor/shader/Texture"
import { type TextureCube } from "@stovokor/shader/TextureCube"
import { type GLTF } from "./GLTF"
import { type GLTFMaterialType, type GLTFTextureInfoType, type GLTFTextureTransformType } from "./GLTFTypes"

export class GLTFMaterial implements Material {

	public baseColorTexture?: Texture
	public baseColorFactor = new vec4(1, 1, 1, 1)
	public baseColorTextureTransform?: matrix

	public metallicRoughnessTexture?: Texture
	public metallicFactor = 0
	public roughnessFactor = 0
	public metallicRoughnessTextureTransform?: matrix

	public emissiveTexture?: Texture
	public emissiveFactor = new vec3(0)
	public emissiveStrength: number = 1.0
	public emissiveTextureTransform?: matrix

	public normalTexture?: Texture
	public normalTextureTransform?: matrix

	public occlusionTexture?: Texture
	public occlusionTextureTransform?: matrix

	public brdfLut?: Texture
	public environmentDiffuse?: TextureCube
	public environmentSpecular?: TextureCube

	public transparency: boolean = false
	public doubleSided: boolean = false
	public alphaMode: "OPAQUE" | "MASK" | "BLEND" = "OPAQUE"
	public alphaCutoff: number = 0.5
	public unlit: boolean = false
	public available: boolean = true

	constructor() {
		this.transparency = false
		this.available = true
	}

	private createTextureTransform(transform: GLTFTextureTransformType): matrix {
		const offset = transform.offset ?? [0, 0]
		const rotation = transform.rotation ?? 0
		const scale = transform.scale ?? [1, 1]

		// Build transform matrix: Translation * Rotation * Scale
		// Per glTF spec: Texcoord = (Transform * vec3(UV, 1.0)).xy
		const cos = Math.cos(rotation)
		const sin = Math.sin(rotation)

		// Matrix in column-major order:
		// [ cos*sx  sin*sx  ox ]
		// [-sin*sy  cos*sy  oy ]
		// [   0       0      1 ]
		const values = [
			cos * scale[0], -sin * scale[1], 0, 0,
			sin * scale[0],  cos * scale[1], 0, 0,
			0, 0, 1, 0,
			offset[0], offset[1], 0, 1
		]

		return new matrix(values)
	}

	public async load(material: GLTFMaterialType, gltf: GLTF): Promise<void> {
		const textureById = new Map<number, Texture>()

		const loadTexture = async (index: number): Promise<Texture> => {
			let texture = textureById.get(index)
			if(texture) {
				return texture
			}
			texture = await gltf.loadTexture(index)
			textureById.set(index, texture)
			return texture
		}

		const loadTextureTransform = (textureInfo: GLTFTextureInfoType | undefined): matrix | undefined => {
			if(!textureInfo?.extensions?.KHR_texture_transform) {
				return undefined
			}
			return this.createTextureTransform(textureInfo.extensions.KHR_texture_transform)
		}

		const roughness = material.pbrMetallicRoughness
		if(roughness) {
			if(roughness.baseColorTexture) {
				this.baseColorTexture = await loadTexture(roughness.baseColorTexture.index)
				this.baseColorTextureTransform = loadTextureTransform(roughness.baseColorTexture)
			}
			if(roughness.baseColorFactor) {
				this.baseColorFactor = new vec4(roughness.baseColorFactor)
			}
			this.metallicFactor = roughness.metallicFactor ?? 0
			this.roughnessFactor = roughness.roughnessFactor ?? 0

			if(roughness.metallicRoughnessTexture) {
				this.metallicRoughnessTexture = await loadTexture(roughness.metallicRoughnessTexture.index)
				this.metallicRoughnessTextureTransform = loadTextureTransform(roughness.metallicRoughnessTexture)
			}
		}

		if(material.normalTexture) {
			this.normalTexture = await loadTexture(material.normalTexture.index)
			this.normalTextureTransform = loadTextureTransform(material.normalTexture)
		}

		if(material.occlusionTexture) {
			this.occlusionTexture = await loadTexture(material.occlusionTexture.index)
			this.occlusionTextureTransform = loadTextureTransform(material.occlusionTexture)
		}

		if(material.emissiveTexture) {
			this.emissiveTexture = await loadTexture(material.emissiveTexture.index)
			this.emissiveTextureTransform = loadTextureTransform(material.emissiveTexture)
		}
		this.emissiveFactor = new vec3(material.emissiveFactor ?? 0)

		// KHR_materials_emissive_strength extension
		if(material.extensions?.KHR_materials_emissive_strength) {
			this.emissiveStrength = material.extensions.KHR_materials_emissive_strength.emissiveStrength
		}

		// Alpha mode handling
		this.alphaMode = material.alphaMode as "OPAQUE" | "MASK" | "BLEND" ?? "OPAQUE"
		this.alphaCutoff = material.alphaCutoff ?? 0.5
		this.transparency = this.alphaMode === "BLEND"

		// Per glTF spec section 3.9.5: doubleSided defaults to false
		this.doubleSided = material.doubleSided ?? false

		// Check for KHR_materials_unlit extension
		if(material.extensions?.KHR_materials_unlit) {
			this.unlit = true
		}
	}

	public requiredAttributes(): Record<string, string> {
		const defines: Record<string, string> = {}

		// Material type
		if(this.unlit) {
			defines.MATERIAL_UNLIT = "1"
		}

		// Alpha mode
		if(this.alphaMode === "MASK") {
			defines.ALPHA_MASK = "1"
		} else if(this.alphaMode === "BLEND") {
			defines.ALPHA_BLEND = "1"
		}

		// Textures
		if(this.baseColorTexture) {
			defines.USE_TEXTURE_BASECOLOR = "1"
			if(this.baseColorTextureTransform) {
				defines.USE_BASECOLOR_TRANSFORM = "1"
			}
		}
		if(this.metallicRoughnessTexture) {
			defines.USE_TEXTURE_METALLICROUGHNESS = "1"
			if(this.metallicRoughnessTextureTransform) {
				defines.USE_METALLICROUGHNESS_TRANSFORM = "1"
			}
		}
		if(this.normalTexture) {
			defines.USE_TEXTURE_NORMAL = "1"
			if(this.normalTextureTransform) {
				defines.USE_NORMAL_TRANSFORM = "1"
			}
		}
		if(this.emissiveTexture) {
			defines.USE_TEXTURE_EMISSIVE = "1"
			if(this.emissiveTextureTransform) {
				defines.USE_EMISSIVE_TRANSFORM = "1"
			}
		}
		if(this.occlusionTexture) {
			defines.USE_TEXTURE_OCCLUSION = "1"
			if(this.occlusionTextureTransform) {
				defines.USE_OCCLUSION_TRANSFORM = "1"
			}
		}
		if(this.environmentDiffuse) {
			defines.USE_IBL = "1"
		}

		return defines
	}
}

export class GLTFMaterialDefault extends GLTFMaterial {
	public baseColorFactor = new vec4(1, 1, 1, 1)
	public metallicFactor = 0
	public roughnessFactor = 0
}